import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    ActivityIndicator
} from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import ListsManager from '../services/ListsManager';
import { useTranslation } from 'react-i18next';

const PhoneNumberScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { listID, listName } = location.state || {};

    const [phoneNumber, setPhoneNumber] = useState('+');
    const [isConfirmButtonVisible, setIsConfirmButtonVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const phoneInputRef = useRef(null);

    useEffect(() => {
        // Focus the phone input when the screen appears
        setTimeout(() => {
            if (phoneInputRef.current) {
                phoneInputRef.current.focus();
            }
        }, 100);
    }, []);

    // Update confirm button visibility when phone number changes
    useEffect(() => {
        setIsConfirmButtonVisible(phoneNumber.length > 1); // Visible if there's at least "+" plus 1 digit
    }, [phoneNumber]);

    const handlePhoneNumberChange = (text) => {
        // Validate the input
        if (!text.startsWith('+')) {
            // Always keep the "+" at the beginning
            text = '+' + text.replace('+', '');
        }

        // Only allow digits after the "+"
        const validText = '+' + text.substring(1).replace(/[^0-9]/g, '');

        // Limit length to 16 characters (+ sign plus 15 digits)
        if (validText.length <= 16) {
            setPhoneNumber(validText);
        }
    };

    const handleConfirm = async () => {
        try {
            setIsLoading(true);
            setError('');

            // Call the backend to request a verification code
            const success = await ListsManager.requestCode(phoneNumber);

            if (success) {
                // Navigate to confirmation code screen
                navigate('/confirmation-code', {
                    state: {
                        phoneNumber,
                        listID,
                        listName
                    }
                });
            } else {
                setError(t('app.phoneNumber.failedRequest'));
            }
        } catch (error) {
            console.error('Error requesting code:', error);
            setError(t('app.phoneNumber.failedRequestError', { error: error.message || 'Unknown error' }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={handleBack}
                >
                    <Text style={styles.backButtonText}>{t('app.common.back')}</Text>
                </TouchableOpacity>
            </View>

            <View style={styles.content}>
                <Text style={styles.title}>{t('app.phoneNumber.title')}</Text>

                <TextInput
                    ref={phoneInputRef}
                    style={styles.phoneInput}
                    value={phoneNumber}
                    onChangeText={handlePhoneNumberChange}
                    keyboardType="phone-pad"
                    placeholder="+1 201-555-0123"
                    placeholderTextColor="#666"
                />

                {error ? <Text style={styles.errorText}>{error}</Text> : null}

                {isLoading ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator size="small" color="#0066cc" />
                        <Text style={styles.loadingText}>{t('app.phoneNumber.requestingCode')}</Text>
                    </View>
                ) : isConfirmButtonVisible && (
                    <TouchableOpacity
                        style={styles.confirmButton}
                        onPress={handleConfirm}
                    >
                        <Text style={styles.confirmButtonText}>{t('app.phoneNumber.confirm')}</Text>
                    </TouchableOpacity>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'var(--bg-deep-void)',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255, 255, 255, 0.08)',
        backgroundColor: 'var(--bg-card)',
    },
    backButton: {
        padding: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        transition: 'all 0.2s',
    },
    backButtonText: {
        color: 'var(--text-primary)',
        fontSize: 16,
        fontWeight: '600',
    },
    content: {
        flex: 1,
        padding: 32,
        alignItems: 'center',
    },
    title: {
        fontSize: 28,
        fontWeight: '700',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: 48,
        marginTop: 24,
        fontFamily: 'Outfit, sans-serif',
        letterSpacing: '-0.01em',
    },
    phoneInput: {
        fontSize: 32,
        fontWeight: '700',
        color: 'var(--text-primary)',
        borderBottomWidth: 2,
        borderBottomColor: 'var(--accent-primary)',
        paddingVertical: 16,
        marginBottom: 64,
        textAlign: 'center',
        width: '100%',
        maxWidth: 320,
        backgroundColor: 'transparent',
        outlineStyle: 'none',
        fontFamily: 'monospace',
        letterSpacing: '0.05em',
    },
    confirmButton: {
        backgroundColor: 'var(--accent-primary)',
        borderRadius: 20,
        paddingVertical: 18,
        alignItems: 'center',
        width: '100%',
        maxWidth: 200,
        alignSelf: 'center',
        marginTop: 24,
        boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
        cursor: 'pointer',
        transition: 'transform 0.1s',
    },
    confirmButtonText: {
        color: 'white',
        fontSize: 18,
        fontWeight: '700',
        letterSpacing: '0.05em',
    },
    errorText: {
        color: 'var(--accent-warning)',
        marginBottom: 32,
        textAlign: 'center',
        fontSize: 16,
        backgroundColor: 'rgba(244, 63, 94, 0.1)',
        padding: 16,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(244, 63, 94, 0.2)',
        width: '100%',
        maxWidth: 400,
    },
    loadingContainer: {
        marginTop: 32,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        padding: 16,
        borderRadius: 16,
    },
    loadingText: {
        color: 'var(--text-secondary)',
        marginLeft: 12,
        fontSize: 16,
    },
});

export default PhoneNumberScreen; 